<!--
  The folder rail: the tree, headed by Root, with the two views that are not
  places in a group below it.

  TASK-042 §2 -- *"All the actions should be on the right-hand side, not on the
  left-hand file menu side."* This component is NAVIGATION ONLY. It has no
  rename, no delete, no create, no menus and no `busy` prop. Every one of those
  moved to the toolbar in the content pane (§3a); none of them was deleted.

  TASK-042 §1 -- **Root is a folder on screen and still not a row.** Its id is
  `null`, the same `null` every other piece of folder state on this screen
  already means the root by; no synthetic uuid is minted, because a sentinel
  that looks like an id is a sentinel that will reach a query. "Unfiled" was the
  name of a predicate; "Root" is the name of a place.

  All documents and Trash sit BELOW the tree behind a rule, because neither is
  a place and neither has ancestors -- and leaving All documents above the tree
  would put a view that is not a place above the place everything is in.

  TASK-042 §9 -- **`role="tree"` with a roving tabindex.** Tab enters the rail
  once. Up and Down move, Right expands or descends, Left collapses or ascends,
  Home and End jump, Enter and Space select. The visible rows are flattened here
  once and the key handler is index arithmetic over that list, so the recursion
  in `FolderBranch` does not have to know anything about its siblings.

  TASK-042 §6b -- **the tree accepts a dropped document, and that is not an
  action.** It adds no control, no label and nothing to click; the rail looks
  exactly as it does with the feature off until something is already being
  dragged over it, and nothing is created, renamed or deleted from here. The
  only thing a drop does is the same `PUT /v1/docs/:id/folder` the Move to…
  dialog calls. It is one prop, `zone`: passing `undefined` -- which is what
  `TREE_ACCEPTS_DROPS = false` does -- makes every handler inert and changes
  nothing else on the screen.

  Styling is scoped to this component on purpose: the shared stylesheets are
  being rewritten in parallel, so nothing here reaches outside its own markup.
-->
<script lang="ts">
  import Icon from './Icon.svelte'
  import { SvelteSet } from 'svelte/reactivity'
  import type { Folder, FolderNode, View } from './doclist-types'
  import { ROOT_GLYPH, ROOT_NAME, buildFolderTree, folderAncestry, sameView } from './doclist-types'
  import type { DropZone } from '../drop'
  import { acceptDrag, leftTarget } from '../drop'
  import FolderBranch from './FolderBranch.svelte'

  let {
    folders,
    view,
    onselect,
    onhover,
    zone,
  }: {
    folders: Folder[]
    view: View
    onselect: (view: View) => void
    /**
     * Pointed at, not clicked. The screen uses it to fetch a place's documents
     * before the click lands, which is what makes a first visit feel like a
     * second one. It is NOT an action: it adds no control, no label and
     * nothing to click, and the rail looks identical with it absent (§2).
     */
    onhover?: (view: View) => void
    /**
     * Decision A, and the gate for it (§6b). Absent means the tree is inert
     * during a drag and looks and behaves exactly as it did before phase 4.
     */
    zone?: DropZone
  } = $props()

  /** The one row a drag is currently over. `null` IS a row: the Root node. */
  let over = $state<string | null | undefined>(undefined)

  function aim(id: string | null, isOver: boolean) {
    // `undefined` is "nothing is aimed at"; `null` is the Root row, which is a
    // real target. Conflating the two loses the root's highlight entirely.
    if (isOver) over = id
    else if (over === id) over = undefined
    zone?.aim(id, isOver)
  }

  // A drag that ends anywhere -- a drop, an Escape, a release over nothing --
  // must not leave a row remembered as "aimed at", or the NEXT drag starts with
  // that row already highlighted and the pointer nowhere near it.
  $effect(() => {
    if (!zone?.active) over = undefined
  })

  const rootAimed = $derived(Boolean(zone?.active) && over === null)
  const rootWelcome = $derived(rootAimed && (zone?.accepts(null) ?? false))
  const rootRefuses = $derived(rootAimed && !rootWelcome)

  function hover(id: string | null) {
    onhover?.(id === null ? { kind: 'root' } : { kind: 'folder', id })
  }

  const tree = $derived<FolderNode[]>(buildFolderTree(folders))

  // A reactive Set: Svelte 5 does not proxy the built-in, so mutating a plain
  // Set would change nothing on screen.
  const expanded = new SvelteSet<string>()
  /*
   * The root is always open, by request: it is the top of the tree and the
   * thing everything else hangs from, and a rail whose only row is `/` tells
   * you nothing. So this is a constant, the twisty beside it is decoration
   * rather than a button, and Left on the root moves rather than collapses.
   */
  const rootOpen = true
  /** The one row with `tabindex="0"`. `null` is the Root node. */
  let active = $state<string | null>(null)
  let railEl = $state<HTMLElement | null>(null)

  /** One flat list of the rows that are actually on screen, in visual order. */
  interface Row {
    id: string | null
    hasChildren: boolean
    open: boolean
    /** `null` is Root; `undefined` is "nothing above this" -- Root itself. */
    parent: string | null | undefined
  }

  const rows = $derived.by(() => {
    const out: Row[] = [
      { id: null, hasChildren: tree.length > 0, open: rootOpen, parent: undefined },
    ]
    const walk = (nodes: FolderNode[], parent: string | null) => {
      for (const node of nodes) {
        const open = expanded.has(node.id)
        out.push({ id: node.id, hasChildren: node.children.length > 0, open, parent })
        if (open) walk(node.children, node.id)
      }
    }
    if (rootOpen) walk(tree, null)
    return out
  })

  // Selecting a folder opens everything above it, and puts the tab stop on it.
  // Without this, clicking a breadcrumb or landing on a folder from a URL shows
  // a list whose folder is nowhere on screen.
  $effect(() => {
    if (view.kind === 'folder') {
      for (const f of folderAncestry(folders, view.id)) if (f.id !== view.id) expanded.add(f.id)
      active = view.id
    } else if (view.kind === 'root') {
      active = null
    }
  })

  function setOpen(id: string, open?: boolean) {
    const next = open ?? !expanded.has(id)
    if (next) expanded.add(id)
    else expanded.delete(id)
  }

  function choose(id: string | null) {
    active = id
    onselect(id === null ? { kind: 'root' } : { kind: 'folder', id })
  }

  function focusRow(id: string | null) {
    active = id
    railEl?.querySelector<HTMLElement>(`[data-tree-id="${id ?? 'root'}"]`)?.focus()
  }

  function key(event: KeyboardEvent, id: string | null) {
    const list = rows
    const i = list.findIndex((r) => r.id === id)
    if (i < 0) return
    const row = list[i]
    let target: string | null | undefined

    switch (event.key) {
      case 'ArrowDown':
        target = list[i + 1]?.id
        break
      case 'ArrowUp':
        target = list[i - 1]?.id
        break
      case 'Home':
        target = list[0]?.id
        break
      case 'End':
        target = list[list.length - 1]?.id
        break
      case 'ArrowRight':
        // Closed with children: open it. Already open: step into it. A leaf
        // does nothing, which is the standard pattern's answer too.
        if (row.hasChildren && !row.open) {
          event.preventDefault()
          // The root is already open and cannot be otherwise.
          if (id !== null) setOpen(id, true)
          return
        }
        if (row.open) target = list[i + 1]?.id
        break
      case 'ArrowLeft':
        // Not on the root: it stays open, so Left there has nothing to do.
        if (row.open && id !== null) {
          event.preventDefault()
          setOpen(id, false)
          return
        }
        target = row.parent
        break
      case 'Enter':
      case ' ':
        event.preventDefault()
        choose(id)
        return
      default:
        return
    }

    event.preventDefault()
    // `undefined` is "there is nothing there"; `null` is the Root row, which is
    // a real destination. The two must not be conflated.
    if (target === undefined) return
    focusRow(target)
  }
</script>

<nav class="rail" aria-label="Folders" bind:this={railEl}>
  <ul class="rail-list" role="tree" aria-label="Folders">
    <li role="none">
      <div
        class="rail-row"
        role="treeitem"
        data-tree-id="root"
        aria-level="1"
        aria-selected={sameView(view, { kind: 'root' }) ? 'true' : undefined}
        aria-expanded={tree.length > 0 ? rootOpen : undefined}
        tabindex={active === null ? 0 : -1}
        aria-label={ROOT_NAME}
        class:drop-welcome={rootWelcome}
        class:drop-refuses={rootRefuses}
        onclick={() => choose(null)}
        onkeydown={(event) => key(event, null)}
        onpointerenter={() => hover(null)}
        ondragenter={(event) => {
          if (!zone) return
          if (acceptDrag(event, zone.accepts(null))) aim(null, true)
        }}
        ondragover={(event) => {
          if (zone) acceptDrag(event, zone.accepts(null))
        }}
        ondragleave={(event) => {
          if (leftTarget(event)) aim(null, false)
        }}
        ondrop={(event) => {
          if (!zone || !acceptDrag(event, zone.accepts(null))) return
          over = undefined
          zone.drop(null, event.dataTransfer)
        }}
      >
        {#if tree.length > 0}
          <!-- Not a button: the root does not close. Drawn open so the row
               still reads as the parent of everything below it. -->
          <span class="rail-twisty open" aria-hidden="true">
            <Icon name="chevron" size="0.95rem" />
          </span>
        {:else}
          <span class="rail-twisty rail-twisty-empty" aria-hidden="true"></span>
        {/if}
        <!-- Drawn as `/`, announced as "Root". The `aria-label` above is on the
             treeitem itself rather than on this span, because the row is the
             thing a screen reader names and the twisty beside it is hidden. -->
        <span class="rail-name rail-root">{ROOT_GLYPH}</span>
      </div>

      {#if rootOpen && tree.length > 0}
        <ul class="rail-list" role="group">
          {#each tree as node (node.id)}
            <FolderBranch
              {node}
              level={2}
              {view}
              {expanded}
              {active}
              ontoggle={setOpen}
              onchoose={choose}
              onkey={key}
              onwarm={hover}
              zone={zone && { ...zone, aim }}
              {over}
            />
          {/each}
        </ul>
      {/if}
    </li>
  </ul>

  {#if folders.length === 0}
    <p class="rail-empty">No folders yet.</p>
  {/if}

  <hr class="rail-rule" />

  <!-- Neither of these is a place: they have no ancestors, nothing is inside
       them, and they are not in the tree. Hence the rule above. -->
  <ul class="rail-list rail-foot">
    <li>
      <button
        type="button"
        class="rail-entry"
        aria-current={sameView(view, { kind: 'all' }) ? 'true' : undefined}
        onclick={() => onselect({ kind: 'all' })}
      >
        <Icon name="documents" />
        <span class="rail-name">All documents</span>
      </button>
    </li>
    <li>
      <button
        type="button"
        class="rail-entry"
        aria-current={sameView(view, { kind: 'archived' }) ? 'true' : undefined}
        onclick={() => onselect({ kind: 'archived' })}
      >
        <Icon name="trash" />
        <span class="rail-name">Trash</span>
      </button>
    </li>
  </ul>
</nav>

<style>
  .rail {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    min-width: 0;
    font-family: var(--font-sans, ui-sans-serif, system-ui, sans-serif);
  }

  .rail-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  .rail-rule {
    margin: 0;
    border: 0;
    border-top: 1px solid var(--color-line, #211d1824);
  }

  .rail-row {
    display: flex;
    align-items: baseline;
    gap: 0.25rem;
    padding: 0.4rem 0.5rem;
    border-radius: 0.375rem;
    cursor: pointer;
  }

  .rail-row:hover {
    background: var(--color-plinth, #f1ece4);
  }

  .rail-row[aria-selected='true'] {
    background: var(--color-plinth, #f1ece4);
    font-weight: 600;
  }

  /* The drop affordance: an outline and a tint, and only while something is
     actually being dragged over the row. `outline` rather than `border`, so
     the row's box does not change and the tree does not shuffle mid-drag. The
     refusing style is DASHED as well as red -- nothing here is conveyed by
     colour alone -- and the reason is said out loud in `dragNotice`. */
  .rail-row.drop-welcome,
  .rail-row.drop-refuses {
    transition: background-color 0.12s ease, outline-color 0.12s ease;
  }

  .rail-row.drop-welcome {
    outline: 2px solid var(--color-ink, #211d18);
    outline-offset: -2px;
    background: var(--color-plinth, #f1ece4);
  }

  .rail-row.drop-refuses {
    outline: 2px dashed var(--color-red-deep, #c01030);
    outline-offset: -2px;
    cursor: not-allowed;
  }

  @media (prefers-reduced-motion: reduce) {
    .rail-row.drop-welcome,
    .rail-row.drop-refuses {
      transition: none;
    }
  }

  .rail-twisty {
    flex: 0 0 auto;
    width: 1.35rem;
    height: 1.35rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 0.25rem;
    background: transparent;
    color: var(--color-ink-soft, #6e665c);
    font: inherit;
    line-height: 1;
    cursor: pointer;
  }

  /* The root's is drawn, not pressed: it never closes. */
  span.rail-twisty {
    cursor: default;
  }

  /* Same single path at two angles as a branch's caret, so the root and its
     children cannot drift apart in weight or baseline. */
  .rail-twisty.open :global(.icon-chevron) {
    rotate: 90deg;
  }

  .rail-twisty:hover {
    color: var(--color-ink, #211d18);
  }

  .rail-twisty-empty {
    cursor: default;
  }

  .rail-entry {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    width: 100%;
    /* Lines up with a folder NAME rather than with its twisty: these are not
       in the tree and should not pretend to be at its root. */
    padding: 0.4rem 0.5rem 0.4rem 1.85rem;
    border: 0;
    border-radius: 0.375rem;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  .rail-entry:hover {
    background: var(--color-plinth, #f1ece4);
  }

  .rail-entry[aria-current='true'] {
    background: var(--color-plinth, #f1ece4);
    font-weight: 600;
  }

  .rail-name {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* `/` is one glyph where a word used to be: it needs the weight, or the root
     row reads as an empty line with a twisty next to it. */
  .rail-root {
    font-family: var(--font-mono, ui-monospace, monospace);
    letter-spacing: 0.05em;
  }

  .rail-empty {
    margin: 0;
    padding: 0.4rem 0.5rem;
    font-size: var(--text-meta, 0.8125rem);
    color: var(--color-ink-soft, #6e665c);
  }
</style>
